/**
 * Product configuration
 * Centralized configuration for all product-specific settings
 */

import { PRODUCT_TYPES, PRODUCT_CODES } from "utils/productDetection";

//DTO
import dtoHeroImage from "assets/products/dto/dto-hero-image.webp";
import marketingGalleryDto1 from "assets/products/dto/dto-marketing-1.webp";
import marketingGalleryDto2 from "assets/products/dto/dto-marketing-2.webp";
import marketingGalleryDto3 from "assets/products/dto/dto-marketing-3.webp";
import marketingGalleryDto4 from "assets/products/dto/dto-marketing-4.webp";

//MLTA
import mltaHeroImage from "assets/products/mlta/mlta-hero-image.webp";
import marketingGalleryMlta1 from "assets/products/mlta/mlta-marketing-1.webp";
import marketingGalleryMlta2 from "assets/products/mlta/mlta-marketing-2.webp";
import marketingGalleryMlta3 from "assets/products/mlta/mlta-marketing-3.webp";
import marketingGalleryMlta4 from "assets/products/mlta/mlta-marketing-4.webp";

//Shared
import logo from "assets/products/shared/icons/aaa-logo.svg";

const dtoName = "Direct Term Life Insurance";
const dtoProductName = "Term Life Insurance";
const mltaName = "Member Loyalty® Accident Insurance";
const giwlName = "Guaranteed Issue Whole Life Insurance";

/**
 * Maps backend feature flag keys to product config feature property names
 * Backend flagKey -> Config feature key
 */
const FEATURE_FLAG_MAPPING = {
    modern_route: "modernRoutes",
    enable_one_inc: "enableOneInc",
};

/**
 * Product configuration object
 * Contains all product-specific settings including branding, assets, and feature flags (can be overridden by backend feature flags)
 */
export const PRODUCT_CONFIG = {
    [PRODUCT_TYPES.DTO]: {
        productType: PRODUCT_TYPES.DTO,
        productCode: PRODUCT_CODES.DTO,
        subdomain: "directterm",
        displayName: dtoName,
        mobileDisplayName: dtoName,
        productName: dtoProductName,
        termBanner: {
            title1: dtoName,
            title2: null,
            subtitle1: "Affordable ",
            subtitle2: "Term ",
            subtitle3: "Life ",
            subtitle4: "Insurance ",
            subtitleMobile1: "Affordable ",
            subtitleMobile2: "Term ",
            subtitleMobile3: null,
            subtitleMobile4: "Life Insurance",
        },
        bannerTexts: {
            left: "Exclusive AAA Member Rates",
            center: "Only 3 Health Questions",
            right: "31-Day Money-Back Guarantee",
        },
        sessionExpiredText: "If you have further questions, please contact Member Services.",
        interaction: {
            applicationMode: "directterm",
            product: "DMT_ONLINE_GROUP",
            channelCode: "DM",
            systemIdCode: "DMTO",
        },
        assets: {
            logo: logo,
            heroImage: dtoHeroImage,
            marketingGallery1: marketingGalleryDto1,
            marketingGallery2: marketingGalleryDto2,
            marketingGallery3: marketingGalleryDto3,
            marketingGallery4: marketingGalleryDto4,
        },
        features: {
            showIdleTimer: false,
            enableWelcomeBack: true,
            modernRoutes: true,
            enableOneInc: false,
        },
    },

    [PRODUCT_TYPES.MLTA]: {
        productType: PRODUCT_TYPES.MLTA,
        productCode: PRODUCT_CODES.MLTA,
        subdomain: "loyalty",
        displayName: mltaName,
        mobileDisplayName: "Accident Insurance",
        productName: mltaName,
        termBanner: {
            title1: mltaName,
            title2: "for AAA members",
            subtitle1: "Affordable ",
            subtitle2: "protection for ",
            subtitle3: "anywhere life ",
            subtitle4: "takes you",
            subtitleMobile1: "Exclusive Member ",
            subtitleMobile2: "Loyalty® ",
            subtitleMobile3: "Protection ",
            subtitleMobile4: "for AAA Members",
        },
        bannerTexts: {
            left: "Exclusive Member Benefits",
            center: "No Medical Exam",
            right: "31-Day Risk-Free Trial",
        },
        sessionExpiredText:
            "We apologize for the inconvenience and appreciate your understanding—it helps us keep your personal information safe.",
        interaction: {
            applicationMode: "loyalty",
            product: "MLTA_ONLINE",
            channelCode: "DM",
            systemIdCode: "MLO",
        },
        assets: {
            logo: logo,
            heroImage: mltaHeroImage,
            marketingGallery1: marketingGalleryMlta1,
            marketingGallery2: marketingGalleryMlta2,
            marketingGallery3: marketingGalleryMlta3,
            marketingGallery4: marketingGalleryMlta4,
        },
        features: {
            showIdleTimer: true,
            enableWelcomeBack: true,
            modernRoutes: true,
            enableOneInc: false,
        },
    },

    // Not currently in use, but reserved for future use
    [PRODUCT_TYPES.GIWL]: {
        productType: PRODUCT_TYPES.GIWL,
        productCode: PRODUCT_CODES.GIWL,
        displayName: giwlName,
        subdomain: "giwl",
        mobileDisplayName: dtoName,
        productName: giwlName,
        termBanner: {
            title1: dtoName,
            title2: null,
            subtitle1: "Affordable ",
            subtitle2: "Term ",
            subtitle3: "Life ",
            subtitle4: "Insurance ",
            subtitleMobile1: "Affordable ",
            subtitleMobile2: "Term ",
            subtitleMobile3: null,
            subtitleMobile4: "Life Insurance",
        },
        bannerTexts: {
            left: "Exclusive AAA Member Rates",
            center: "Only 3 Health Questions",
            right: "31-Day Money-Back Guarantee",
        },
        sessionExpiredText: "If you have further questions, please contact Member Services.",
        interaction: {
            applicationMode: "giwl",
            product: "GIWL",
            channelCode: "Wb",
            systemIdCode: "AAA_LIFE",
            channelId: 10,
            emailSource: "14",
            phoneSource: "14",
        },
        assets: {
            logo: logo,
            heroImage: dtoHeroImage,
            marketingGallery1: marketingGalleryDto1,
            marketingGallery2: marketingGalleryDto2,
            marketingGallery3: marketingGalleryDto3,
            marketingGallery4: marketingGalleryDto4,
        },
        features: {
            showIdleTimer: false,
            enableWelcomeBack: true,
            modernRoutes: true, // Will use modern routes from day 1
            enableOneInc: false,
        },
    },
};

/**
 * Gets the configuration for a specific product type
 * @param {string} productType - The product type ('dto', 'mlta', or 'giwl')
 * @param {Array} featureFlags - Optional array of feature flags from backend
 * @returns {object} Product configuration with feature flag overrides applied
 */
export const getProductConfig = (productType, featureFlags = []) => {
    const config = PRODUCT_CONFIG[productType] || PRODUCT_CONFIG[PRODUCT_TYPES.DTO];

    // Build feature flag overrides from backend flags
    const featureFlagOverrides = {};
    if (featureFlags && featureFlags.length > 0) {
        featureFlags.forEach((flag) => {
            const configKey = FEATURE_FLAG_MAPPING[flag.flagKey];
            if (configKey && flag.isEnabled !== undefined) {
                featureFlagOverrides[configKey] = flag.isEnabled;
            }
        });
    }

    // Apply feature flag overrides to the config
    return {
        ...config,
        features: {
            ...config.features,
            ...featureFlagOverrides,
        },
    };
};
